import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './FlyerAnalysis.css';
import { getCountryName, sortCountries, ALL_COUNTRIES } from './countries';

const API_BASE = process.env.REACT_APP_SERVER_DOMAIN;

const ImagePlaceholder = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/>
    <polyline points="21 15 16 10 5 21"/>
  </svg>
);

export default function FlyerAnalysisBrowse() {
  const { getAccessTokenSilently } = useAuth0();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [country, setCountry] = useState(() => searchParams.get('country') || localStorage.getItem('fa_country') || '');
  const [countries, setCountries] = useState(ALL_COUNTRIES);
  const [retailers, setRetailers] = useState([]);
  const [retailerFilter, setRetailerFilter] = useState(searchParams.get('retailer') || '');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [flyers, setFlyers] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const LIMIT = 20;

  // Fetch countries
  useEffect(() => {
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await axios.get(`${API_BASE}/api/flyer-analysis/s3/countries`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCountries(sortCountries(res.data.countries || []));
      } catch (err) {
        console.error('Error fetching countries:', err);
      }
    })();
  }, [getAccessTokenSilently]);

  // Fetch retailers for filter
  useEffect(() => {
    if (!country) return;
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await axios.get(`${API_BASE}/api/flyer-analysis/s3/retailers?country=${country}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setRetailers(res.data.retailers || []);
      } catch (err) {
        console.error('Error fetching retailers:', err);
      }
    })();
  }, [getAccessTokenSilently, country]);

  const fetchFlyers = useCallback(async (newOffset = 0) => {
    if (!country) return;
    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const params = new URLSearchParams({
        country,
        limit: String(LIMIT),
        offset: String(newOffset),
      });
      if (retailerFilter) params.set('retailer_slug', retailerFilter);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);

      const res = await axios.get(`${API_BASE}/api/flyer-analysis/flyers?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (newOffset === 0) {
        setFlyers(res.data.flyers || []);
      } else {
        setFlyers(prev => [...prev, ...(res.data.flyers || [])]);
      }
      setTotal(res.data.total || 0);
      setOffset(newOffset);
    } catch (err) {
      console.error('Error fetching flyers:', err);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, country, retailerFilter, dateFrom, dateTo]);

  useEffect(() => {
    fetchFlyers(0);
  }, [fetchFlyers]);

  const handleCountryChange = (val) => {
    setCountry(val);
    localStorage.setItem('fa_country', val);
    setSearchParams({ country: val });
    setRetailerFilter('');
    setOffset(0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  };

  return (
    <div className="main-column" style={{ display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div className="fa-header">
        <div className="fa-header-left">
          <div>
            <h1 className="fa-header-title">Browse Flyers</h1>
            <p className="fa-header-subtitle">
              {total > 0 ? `${total} flyers found` : 'Explore analyzed flyers'}
            </p>
          </div>
        </div>
        <div className="fa-header-actions">
          <select className="fa-country-select" value={country} onChange={(e) => handleCountryChange(e.target.value)}>
            <option value="" disabled>Select Country</option>
            {countries.map(c => <option key={c} value={c}>{getCountryName(c)}</option>)}
          </select>
        </div>
      </div>

      {/* Sub-navigation */}
      <div className="fa-subnav">
        <a href="/flyer-analysis" className="fa-subnav-link">Dashboard</a>
        <a href="/flyer-analysis/new-job" className="fa-subnav-link">New Job</a>
        <a href={`/flyer-analysis/browse?country=${country}`} className="fa-subnav-link active">Browse Flyers</a>
        <a href={`/flyer-analysis/insights?country=${country}`} className="fa-subnav-link">Insights</a>
      </div>

      <div className="fa-scroll-content">
        {/* Filters */}
        <div className="fa-filters">
          <div className="fa-form-group">
            <label className="fa-form-label">Retailer</label>
            <select className="fa-form-select" value={retailerFilter} onChange={(e) => setRetailerFilter(e.target.value)}>
              <option value="">All Retailers</option>
              {retailers.map(r => <option key={r.slug} value={r.slug}>{r.slug}</option>)}
            </select>
          </div>
          <div className="fa-form-group">
            <label className="fa-form-label">Date From</label>
            <input type="date" className="fa-form-input" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div className="fa-form-group">
            <label className="fa-form-label">Date To</label>
            <input type="date" className="fa-form-input" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
        </div>

        {loading && flyers.length === 0 ? (
          <div className="fa-loading">Loading flyers...</div>
        ) : flyers.length === 0 ? (
          <div className="fa-empty">
            <ImagePlaceholder />
            <p>No flyers found</p>
            <p className="fa-empty-subtitle">Try adjusting your filters or analyze some flyers first</p>
          </div>
        ) : (
          <>
            <div className="fa-flyer-grid">
              {flyers.map((flyer, i) => (
                <div
                  key={`${flyer.retailer_slug}-${flyer.flyer_date}-${i}`}
                  className="fa-flyer-card"
                  onClick={() => navigate(`/flyer-analysis/flyer/${flyer.retailer_slug}/${flyer.flyer_date || '_nodate'}?country=${country}`)}
                >
                  {flyer.thumbnail_url ? (
                    <img className="fa-flyer-thumb" src={flyer.thumbnail_url} alt={flyer.retailer_name}
                      onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                  ) : null}
                  <div className="fa-flyer-thumb-placeholder" style={flyer.thumbnail_url ? { display: 'none' } : {}}>
                    <ImagePlaceholder />
                  </div>
                  <div className="fa-flyer-info">
                    <h4 className="fa-flyer-retailer">{flyer.retailer_name}</h4>
                    <p className="fa-flyer-date">{formatDate(flyer.flyer_date)}</p>
                    <div className="fa-flyer-badges">
                      <span className="fa-flyer-badge">{flyer.total_pages} pages</span>
                      <span className="fa-flyer-badge orange">{flyer.total_products} products</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {flyers.length < total && (
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <button className="fa-btn outline" onClick={() => fetchFlyers(offset + LIMIT)} disabled={loading}>
                  {loading ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
